import i18n from 'i18next';
import deburr from 'lodash/deburr';
import moment from 'moment';
import * as Yup from 'yup';

import { RegistrationApi } from 'api';
import { CUSTOM_REGEX, PASSPORT, PERSONAL_ID, SUPPORTED_IMAGE_FORMATS } from 'shared/constants/common';
import { getBalanceDetails, peselAgeValidation, peselValueValidation } from 'shared/util/utility';
import { FINANCIAL_THRESHOLDS } from './financial-thresholds';

export const loginFormValidationSchema = () =>
	Yup.object().shape({
		email: Yup.string()
			.matches(CUSTOM_REGEX.email, `${i18n.t('emailFieldValidation')}. ${i18n.t('emailFormatText')}`)
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('email') }))
			.strict(true),
		password: Yup.string()
			.matches(new RegExp('(?=.{8,})'), `${i18n.t('inValidPassword')}. ${i18n.t('passwordFormatText')}`)
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('password') }))
			.strict(true),
	});

export const forgotPassFormValidationSchema = () =>
	Yup.object().shape({
		email: Yup.string()
			.matches(CUSTOM_REGEX.email, `${i18n.t('emailFieldValidation')}. ${i18n.t('emailFormatText')}`)
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('email') }))
			.strict(true),
	});

export const resetPassFormValidationSchema = () =>
	Yup.object().shape({
		newPassword: Yup.string()
			.matches(CUSTOM_REGEX.password, i18n.t('typeValidation', { type: i18n.t('passwordConstraintText') }))
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('newPassword') }))
			.strict(true),
		confirmNewPassword: Yup.string()
			.matches(CUSTOM_REGEX.password, i18n.t('passwordFieldValidation'))
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('confirmNewPassword') }))
			.strict(true)
			.test(function (value) {
				const { newPassword } = this.parent;
				if (value === newPassword) return true;
				else
					return this.createError({
						message: i18n.t('passwordMatchValidation'),
					});
			}),
	});

export const changePassFormValidationSchema = () =>
	Yup.object().shape({
		oldPassword: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('oldPassword') }))
			.strict(true),
		newPassword: Yup.string()
			.matches(CUSTOM_REGEX.password, i18n.t('inValidPassword'))
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('newPassword') }))
			.strict(true),
		confirmNewPassword: Yup.string()
			.matches(CUSTOM_REGEX.password, i18n.t('inValidPassword'))
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('confirmNewPassword') }))
			.strict(true)
			.test(function (value) {
				const { newPassword } = this.parent;
				if (value === newPassword) return true;
				else
					return this.createError({
						message: i18n.t('passwordMatchValidation'),
					});
			}),
	});

const peselNumberValidationFunction = async (value: string | undefined, resolve: any, createError: any) => {
	const resp = await RegistrationApi.checkUniqueness({
		field: 'personalIdentifier',
		value: value,
	});

	if (resp && resp.data && !resp.data.isError && resp.data.data) {
		resolve(true);
	} else if (resp) {
		resolve(
			createError({
				message: i18n.t('alreadyExistsValidation', {
					field: i18n.t('peselNum'),
				}),
			}),
		);
	} else {
		resolve(
			createError({
				message: i18n.t('typeValidation', { type: i18n.t('peselNum') }),
			}),
		);
	}
};

export const formatPhoneNumber = (phoneNumber: string) => {
	return phoneNumber.replaceAll(' ', '').replace('+48', '');
};

const phoneNumValidationFunction = async (value: string | undefined, resolve: any, createError: any) => {
	const resp = await RegistrationApi.checkUniqueness({
		field: 'mobile',
		value: value ? formatPhoneNumber(value) : value,
	});

	if (resp && resp.data && !resp.data.isError && resp.data.data) {
		resolve(true);
	} else if (resp) {
		resolve(
			createError({
				message: i18n.t('alreadyExistsValidation', {
					field: i18n.t('phoneNum'),
				}),
			}),
		);
	} else {
		resolve(
			createError({
				message: `${i18n.t('typeValidation', {
					type: i18n.t('phoneNum'),
				})}.`,
			}),
		);
	}
};

const validateEmail = async (email: string, resolve: any, createError: any, checkUniquenessDebounce?: any) => {
	if (email) {
		if (CUSTOM_REGEX.email.test(email)) {
			const resp = await (checkUniquenessDebounce ? checkUniquenessDebounce : RegistrationApi.checkUniqueness).current({
				field: 'email',
				value: email,
			});

			if (resp && resp.data && !resp.data.isError && resp.data.data) {
				resolve(true);
			} else {
				resolve(
					createError({
						message: i18n.t('alreadyExistsValidation', { field: i18n.t('email') }),
					}),
				);
			}
		} else {
			resolve(
				createError({
					message: `${i18n.t('typeValidation', { type: i18n.t('email') })}. ${i18n.t('emailFormatText')}`,
				}),
			);
		}
	}
	resolve(
		createError({
			message: i18n.t('requiredFieldValidation', { field: i18n.t('email') }),
		}),
	);
};

const getDateBefore18 = () => {
	const d = new Date();
	return new Date(d.getFullYear() - 18, d.getMonth(), d.getDate());
};

const firstNameValidation = () =>
	Yup.object().shape({
		firstName: Yup.string()
			.min(2, i18n.t('nameTooShort'))
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('firstName') }))
			.strict(true),
	});

const lastNameValidation = () =>
	Yup.object().shape({
		lastName: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('lastName') }))
			.strict(true),
	});

export const passwordValidation = () =>
	Yup.object().shape({
		password: Yup.string()
			.matches(CUSTOM_REGEX.password, i18n.t('inValidPassword'))
			.test({
				test: (value) => value === deburr(value),
				message: i18n.t('inValidPassword'),
			})
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('password') }))
			.strict(true),
	});

export const emailValidation = (checkUniquenessDebounce?: any) =>
	Yup.object().shape({
		// email: Yup.string()
		// .matches(CUSTOM_REGEX.email, `${i18n.t('emailFieldValidation')}. ${i18n.t('emailFormatText')}`)
		// 	.required(i18n.t('requiredFieldValidation', { field: i18n.t('email') })),

		email: Yup.string().test(async function (value) {
			if (!value) {
				return this.createError({
					message: i18n.t('requiredFieldValidation', { field: i18n.t('email') }),
				});
			}
			if (CUSTOM_REGEX.email.test(value)) {
				return new Promise((resolve) => validateEmail(value, resolve, this.createError, checkUniquenessDebounce));
			} else {
				return this.createError({ message: `${i18n.t('emailFieldValidation')}. ${i18n.t('emailFormatText')}` });
			}
		}),
	});

const registrationCodeValidation = () =>
	Yup.object().shape({
		regCode: Yup.string().optional().nullable(),
	});

export const phoneNumValidation = () =>
	Yup.object().shape({
		phoneNum: Yup.string().test(async function (value) {
			if (value) {
				if (CUSTOM_REGEX.phoneNumber.test(value)) {
					return new Promise((resolve) => phoneNumValidationFunction(value, resolve, this.createError));
				} else {
					return this.createError({
						message: `${i18n.t('phoneNum_invalid')}. ${
							value.replaceAll(' ', '').replaceAll('-', '').length < 9 ? i18n.t('phoneNumberFormatText') : ''
						}`,
					});
				}
			}
			return this.createError({
				message: i18n.t('requiredFieldValidation', { field: i18n.t('phoneNum') }),
			});
		}),
	});

export const smsCodeValidation = () =>
	Yup.object().shape({
		smsCode: Yup.string().test(async function (value) {
			if (value) {
				const sanitizedSmsCode = value.replace(/[^0-9]/g, '');
				const slicedSmsCode = sanitizedSmsCode.slice(0, 4);

				if (slicedSmsCode.length === 4) {
					return true;
				} else {
					return this.createError({
						message: i18n.t('smsCodeValidationText'),
					});
				}
			}
			return this.createError({
				message: i18n.t('requiredFieldValidation', { field: i18n.t('smsCode') }),
			});
		}),
	});

const identificationValidation = () =>
	Yup.object().shape({
		peselNum: Yup.string()
			.optional()
			.test(async function (value) {
				const { peselNumNotAvailable } = this.parent;
				if (peselNumNotAvailable) return true;
				if (value) {
					if (CUSTOM_REGEX.peselNumber.test(value)) {
						// pesel age validation - if is above 18 years its true
						// after checking the age - we check if pesel value is proper
						if (!peselValueValidation(value)) {
							return this.createError({
								message: i18n.t('peselValueValidation'),
							});
						} else if (peselAgeValidation(value)) {
							return new Promise((resolve) => peselNumberValidationFunction(value, resolve, this.createError));
						} else {
							return this.createError({
								message: i18n.t('peselAgeValidationText', { field: i18n.t('peselNum') }),
							});
						}
					} else {
						return this.createError({
							message: i18n.t('peselValidationText'),
						});
					}
				}
				return this.createError({
					message: i18n.t('requiredFieldValidation', { field: i18n.t('peselNum') }),
				});
			}),
		peselNumNotAvailable: Yup.boolean().optional().nullable(),
		dob: Yup.date().when('peselNumNotAvailable', {
			is: true,
			then: Yup.date()
				.nullable()
				.strict(false)
				.required(i18n.t('requiredFieldValidation', { field: i18n.t('dob') }))
				.test(function (value) {
					const { peselNumNotAvailable } = this.parent;
					if (!peselNumNotAvailable) return true;

					const date = moment(value);
					if (!date.isValid()) {
						return this.createError({
							message: i18n.t('typeValidation', { type: i18n.t('date') }),
						});
					}
					return true;
				})
				.max(getDateBefore18(), i18n.t('peselAgeValidationText'))
				.min(new Date('01/01/1900'), i18n.t('typeValidation', { type: i18n.t('dob') }))
				.typeError(i18n.t('typeValidation', { type: i18n.t('date') })),
		}),
		country: Yup.string().test(function (value) {
			const { peselNumNotAvailable } = this.parent;
			if (value || !peselNumNotAvailable) return true;
			return this.createError({
				message: i18n.t('requiredFieldValidation', { field: i18n.t('country') }),
			});
		}),
	});

const registrationConsentValidation = () =>
	Yup.object().shape({
		ageConsent: Yup.boolean().required().oneOf([true]),
		allConsent: Yup.boolean().optional().nullable(),
		emailConsent: Yup.boolean().test(function (value) {
			const { allConsent } = this.parent;
			if (value || !allConsent) return true;
			return this.createError({
				message: i18n.t('genericRequiredFieldValidation'),
			});
		}),
		smsConsent: Yup.boolean().test(function (value) {
			const { allConsent } = this.parent;
			if (value || !allConsent) return true;
			return this.createError({
				message: i18n.t('genericRequiredFieldValidation'),
			});
		}),
	});

export const getRegistrationFieldValidators = (step: number, checkUniquenessDebounce?: any) => {
	switch (step) {
		case 1:
			return firstNameValidation();
		case 2:
			return lastNameValidation();
		case 3:
			return emailValidation(checkUniquenessDebounce);
		case 4:
			return passwordValidation();
		case 5:
			return identificationValidation();
		case 6:
			return phoneNumValidation().concat(
				Yup.object().shape({
					emailVerification: Yup.boolean().optional().nullable(),
				}),
			);
		case 7:
			return smsCodeValidation();
		case 8:
			return registrationCodeValidation();
		case 9:
			return registrationConsentValidation();
		default:
			return;
	}
};

export const newdeposit = () =>
	Yup.object().shape({
		amount: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('amount') }))
			.test(function (value) {
				if (value) {
					if (value < FINANCIAL_THRESHOLDS.MIN_DEPOSIT_AMOUNT) {
						return this.createError({
							message: i18n.t('minValueFieldValidation', {
								field: i18n.t('amount'),
								value: FINANCIAL_THRESHOLDS.MIN_DEPOSIT_AMOUNT,
							}),
						});
					} else {
						return true;
					}
				} else {
					return this.createError({
						message: i18n.t('anyAmountFieldValidation'),
					});
				}
			}),
		expirationDate: Yup.string().matches(/^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/, 'Must be in the format MM/DD'),
		// bankId: Yup.number().required(i18n.t('selectBank')),
		// bankTransferConsent: Yup.boolean().optional()
		// .oneOf([true], i18n.t('genericRequiredFieldValidation')),
	});

export const payoutValidationSchema = () =>
	Yup.object().shape({
		amount: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('amount') }))
			.test(function (value) {
				if (value === undefined || value === null || isNaN(value)) {
					return this.createError({
						message: i18n.t('requiredFieldValidation', { field: i18n.t('amount') }),
					});
				}
				if (value > getBalanceDetails().mainBalance) {
					return this.createError({
						message: i18n.t('notEnoughFunds'),
					});
				} else if (value < FINANCIAL_THRESHOLDS.MIN_PAYOUT) {
					return this.createError({
						message: i18n.t('minValueFieldValidation', {
							field: i18n.t('amount'),
							value: FINANCIAL_THRESHOLDS.MIN_PAYOUT,
						}),
					});
				} else {
					return true;
				}
			}),
	});

export const promoCodeFormValidationSchema = () =>
	Yup.object().shape({
		promoCode: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('promoCode') }))
			.strict(true),
	});

// VERIFICATION

export const postalCodeValidationSchema = () =>
	Yup.string()
		.notOneOf(['00-000'], i18n.t('specifyCorrectPostalCode'))
		.test(function (value) {
			if (value) {
				if (CUSTOM_REGEX.postalCode.test(value)) return true;
				else
					return this.createError({
						message: `${i18n.t('typeValidation', {
							type: i18n.t('postalcode'),
						})}. ${i18n.t('postalCodeFormatText')}`,
					});
			}
			return this.createError({
				message: i18n.t('requiredFieldValidation', {
					field: i18n.t('postalcode'),
				}),
			});
		});

export const cityValidationSchema = () =>
	Yup.string()
		.required(i18n.t('requiredFieldValidation', { field: i18n.t('city') }))
		.min(2, i18n.t('cityLengthValidation'))
		.strict(true);

const accVerfStep1FormValidationSchema = () =>
	Yup.object().shape({
		city: cityValidationSchema(),
		postalcode: postalCodeValidationSchema(),
	});

export const accVerfStep2FormValidationSchema = () =>
	Yup.object().shape({
		street: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('street') }))
			.min(2, i18n.t(''))
			.matches(CUSTOM_REGEX.atLeastTwoLetters, {
				message: i18n.t('streetNameLetterValidation'),
			})

			.strict(true),
		buildingNumber: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('buildingNumber') }))
			.matches(CUSTOM_REGEX.atLeastOneDigit, {
				message: i18n.t('buildingNumberDigitValidation'),
			})
			.strict(true),
		apartmentNumber: Yup.string().optional().nullable(),
	});

const accVerfStep5FormValidationSchema = () =>
	Yup.object().shape({
		documentType: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('documentType') }))
			.strict(true),
		uploadFront: Yup.mixed()
			.test(function (value) {
				if (value && value.size > 5 * 1024 * 1024) {
					return this.createError({ message: i18n.t('imageSizeExceededVerifiction') });
				} else if (value && !SUPPORTED_IMAGE_FORMATS.includes(value.type)) {
					return this.createError({ message: i18n.t('wrongFileFormatVerification') });
				} else {
					return true;
				}
			})
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('uploadFront') }))
			.strict(true),
		uploadBack: Yup.mixed().test(function (value) {
			const { documentType } = this.parent;
			if (documentType === PASSPORT) {
				return true;
			}

			if (value && value.size > 5 * 1024 * 1024) {
				return this.createError({ message: i18n.t('imageSizeExceededVerifiction') });
			} else if (value && !SUPPORTED_IMAGE_FORMATS.includes(value.type)) {
				return this.createError({ message: i18n.t('wrongFileFormatVerification') });
			} else if (!value) {
				return this.createError({
					message: i18n.t('requiredFieldValidation', {
						field: i18n.t('uploadBack'),
					}),
				});
			} else {
				return true;
			}
		}),
	});

const accVerfStep6FormValidationSchema = () =>
	Yup.object().shape({
		seriesNumber: Yup.string().test(function (value) {
			const { documentType } = this.parent;
			if (value) {
				if (documentType !== PERSONAL_ID) return true;
				if (CUSTOM_REGEX.seriesAndIdNumber.test(value)) return true;
				else
					return this.createError({
						message: `${i18n.t('typeValidation', {
							type: i18n.t('seriesNumber'),
						})}. ${i18n.t('seriesNumberFormatText')}`,
					});
			}
			return this.createError({
				message: i18n.t('requiredFieldValidation', {
					field: i18n.t('seriesNumber'),
				}),
			});
		}),
		expirationDate: Yup.date()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('expirationDate') }))
			.min(
				moment().add(0, 'days'),
				i18n.t('shouldNotPastDate', { field: i18n.t('expirationDate'), value: moment(new Date()).format('DD/MM/YYYY') }),
			)
			.typeError(i18n.t('typeValidation', { type: i18n.t('expirationDate') }))
			.strict(true),
	});

export const accVerfStep3FormValidationSchema = () =>
	Yup.object().shape({
		bankAccountNumber: Yup.string().test(function (value) {
			if (!value) {
				return this.createError({
					message: i18n.t('requiredFieldValidation', {
						field: i18n.t('bankAccountNumber'),
					}),
				});
			}

			value = value.replaceAll(' ', '');

			if (!CUSTOM_REGEX.bankAccountNumber.test(value)) {
				return this.createError({
					message: `${i18n.t('bankAccountNumberWrong', {
						type: i18n.t('bankAccountNumber'),
					})}. ${i18n.t('bankAccountNumberFormatText')}`,
				});
			}

			//check if the bank account number is a revolut bank account
			const thirdToSixthDigits = value.substring(2, 6);

			if (thirdToSixthDigits === '2910') {
				return this.createError({
					message: i18n.t('bankAccountNumberRevolutAlert'),
				});
			}

			if (!checkBankAccountNumber(value)) {
				return this.createError({
					message: `${i18n.t('bankAccountNumberWrong', {
						type: i18n.t('bankAccountNumber'),
					})}. ${i18n.t('bankAccountNumberBadChecksum')}`,
				});
			}

			return true;
		}),
	});

const accVerfStep4FormValidationSchema = () =>
	Yup.object().shape({
		isPEP: Yup.boolean()
			.oneOf([true, false])
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('isPEP') })),
		giifType: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('sourceOfIncome') }))
			.strict(true),
		nonDictionaryType: Yup.string()
			.when('giifType', {
				is: '100',
				then: Yup.string().required(i18n.t('genericRequiredFieldValidation')),
			})

			.strict(true),
	});

export const myBetFormValidationSchema = () =>
	Yup.object().shape({
		content: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('contentOfBet') }))
			.strict(true)
			.min(1)
			.max(280),
		iAccept: Yup.boolean()
			.required()
			.oneOf([true])
			.test({
				test: (value) => value === true,
				message: i18n.t('genericRequiredFieldValidation'),
			}),
	});

export const getVerificationFieldValidators = (step: number) => {
	switch (step) {
		case 1:
			return accVerfStep1FormValidationSchema();
		case 2:
			return accVerfStep2FormValidationSchema();
		case 3:
			return accVerfStep3FormValidationSchema();
		case 4:
			return accVerfStep4FormValidationSchema();
		case 5:
			return accVerfStep5FormValidationSchema();
		case 6:
			return accVerfStep6FormValidationSchema();
		default:
			return;
	}
};

export const couponNameValidationSchema = () =>
	Yup.object().shape({
		customName: Yup.string()
			.required(i18n.t('requiredFieldValidation', { field: i18n.t('couponName') }))
			.max(40, i18n.t('max40', { field: i18n.t('couponName') })),
	});

export const limitsValidationSchema = () =>
	Yup.object().shape({
		dailyStakeLimit: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('dailyStakeLimit') }))
			.transform((value, originalValue) => {
				if (typeof originalValue === 'string') {
					return parseFloat(originalValue.replace(',', '.'));
				}
				return value;
			})
			.required(i18n.t('limitsMinValueValidation', { field: i18n.t('dailyStakeLimit'), value: 2, unit: i18n.t('currency') }))
			.min(
				FINANCIAL_THRESHOLDS.MIN_STAKE,
				i18n.t('limitsMinValueValidation', {
					field: i18n.t('dailyStakeLimit'),
					value: FINANCIAL_THRESHOLDS.MIN_STAKE,
					unit: i18n.t('currency'),
				}),
			)
			.max(1000000, i18n.t('limitsMaxValueValidation', { field: i18n.t('dailyStakeLimit'), value: 1000000, unit: i18n.t('currency') })),
		monthlyStakeLimit: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('monthlyStakeLimit') }))
			.transform((value, originalValue) => {
				if (typeof originalValue === 'string') {
					return parseFloat(originalValue.replace(',', '.'));
				}
				return value;
			})
			.required(i18n.t('limitsMaxValueValidation', { field: i18n.t('monthlyStakeLimit'), value: 50, unit: i18n.t('currency') }))
			.min(50, i18n.t('limitsMinValueValidation', { field: i18n.t('monthlyStakeLimit'), value: 50, unit: i18n.t('currency') }))
			.max(10000000, i18n.t('limitsMaxValueValidation', { field: i18n.t('monthlyStakeLimit'), value: 10000000, unit: i18n.t('currency') })),
		dailyGameLimit: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('dailyGameLimit') }))
			.transform((value, originalValue) => {
				if (typeof originalValue === 'string') {
					return parseFloat(originalValue.replace(',', '.'));
				}
				return value;
			})
			.required(i18n.t('limitsMinValueValidation', { field: i18n.t('dailyGameLimit'), value: 1, unit: 'H' }))
			.min(1, i18n.t('limitsMinValueValidation', { field: i18n.t('dailyGameLimit'), value: 1, unit: 'H' }))
			.max(24, i18n.t('limitsMaxValueValidation', { field: i18n.t('dailyGameLimit'), value: 24, unit: 'H' })),
		monthlyGameLimit: Yup.number()
			.typeError(i18n.t('requiredFieldValidation', { field: i18n.t('monthlyGameLimit') }))
			.transform((value, originalValue) => {
				if (typeof originalValue === 'string') {
					return parseFloat(originalValue.replace(',', '.'));
				}
				return value;
			})
			.required(i18n.t('limitsMinValueValidation', { field: i18n.t('monthlyGameLimit'), value: 10, unit: 'H' }))
			.min(10, i18n.t('limitsMinValueValidation', { field: i18n.t('monthlyGameLimit'), value: 10, unit: 'H' }))
			.max(745, i18n.t('limitsMaxValueValidation', { field: i18n.t('monthlyGameLimit'), value: 745, unit: 'H' })),
	});

const checkBankAccountNumber = (number: string) => {
	number = number.replaceAll(' ', '');

	// Remove PL from the front to simplify the validation.
	if (number.startsWith('PL')) {
		number = number.substring(2);
	}

	if (number.length !== 26) {
		return false;
	}

	// This is just PL converted to a number P=25, L=21.
	const countryCode = 2521;

	// Add the country code to the end and move first 2 characters after that.
	number = number.substring(2) + countryCode + number.substring(0, 2);

	// Calculate remainder the long way because JS doesn't really do remainders of big numbers.
	const remainder = Array.from(number)
		.map((c) => parseInt(c))
		.reduce((remainder, value) => (remainder * 10 + value) % 97, 0);

	return remainder === 1;
};
